import { AirportListType, getTypedLastSearch, parseBoolean, SearchFieldEnum, useAirportAutocompleterResults, useGlobalContext, useSearch, } from '@honeycomb/data';
import { Button, FlexGrid, useBreakpoints } from '@honeycomb/ui-core';
import React, { useState, useReducer, useMemo, useCallback, useEffect } from 'react';
import { searchCreatedRouting } from '@honeycomb/routing';
import { AirportAutocompleter } from '../../search/holiday-hotel/AirportAutocompleter';
import { SearchContext } from '../../search/SearchContext';
import { getSearchDisplayType, getSearchError } from '../../search/SearchUtil';
import { SearchDispatchActionType, searchReducer } from '../../search/SearchReducer';
import { FlightUpsellCabinSelector } from './FlightUpsellCabinSelector';
import { getFilghtUpsellUpdatedState } from '../../../utils';
function useDepartureAirports() {
    return React.useMemo(() => {
        var _a;
        const lastSearch = getTypedLastSearch();
        return (_a = lastSearch === null || lastSearch === void 0 ? void 0 : lastSearch.departureAirports) !== null && _a !== void 0 ? _a : [];
    }, []);
}
export function HotelFlightUpsellSearch({ state, hotelDetails, selectedElementRef, }) {
    var _a;
    const { resourceStrings: { SEARCH_DEPARTURE_ERROR_EMPTY, FLIGHT_UPSELL_BUTTON_TEXT }, configSettings: { ProductCacheEnabled }, } = useGlobalContext();
    const { largeUp } = useBreakpoints();
    const displayType = getSearchDisplayType(largeUp);
    const [_, setSearch] = useSearch([searchCreatedRouting]);
    const [searchClicked, setSearchClicked] = useState(false);
    const { createSearch, loading } = setSearch;
    const [fetchDepartureAirportResults, departureAirportAutocompleterResults] = useAirportAutocompleterResults();
    const fetchDepartureAirports = useCallback((term) => {
        return fetchDepartureAirportResults({
            searchTerm: term,
            airportList: AirportListType.Departure,
        });
    }, [fetchDepartureAirportResults]);
    const { isCached, name, estabId } = hotelDetails;
    const isCacheEnabled = parseBoolean(ProductCacheEnabled);
    const [searchState, searchDispatch] = useReducer(searchReducer, state);
    const [openField, setOpenField] = useState(SearchFieldEnum.NONE);
    const [airportError, setAirportError] = useState(false);
    const validAirport = Boolean((_a = searchState.departureAirports) === null || _a === void 0 ? void 0 : _a.length);
    const departureAirports = useDepartureAirports();
    const fieldValidity = useMemo(() => ({
        [SearchFieldEnum.AIRPORTS]: { valid: validAirport, error: airportError },
    }), [airportError, validAirport]);
    const openNextField = useCallback(() => {
        if (!validAirport) {
            setOpenField(SearchFieldEnum.AIRPORTS);
            return;
        }
        setOpenField(SearchFieldEnum.NONE);
    }, [validAirport]);
    const handleOpen = useCallback((field) => {
        var _a;
        (_a = selectedElementRef === null || selectedElementRef === void 0 ? void 0 : selectedElementRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setOpenField(field || SearchFieldEnum.NONE);
    }, [selectedElementRef]);
    useEffect(() => {
        searchDispatch({
            type: SearchDispatchActionType.ADD_DEPARTURE_AIRPORTS,
            departureAirports,
        });
    }, [departureAirports]);
    useEffect(() => {
        if (validAirport && airportError) {
            setAirportError(false);
        }
    }, [airportError, validAirport]);
    const searchError = getSearchError(fieldValidity);
    const searchContext = React.useMemo(() => ({
        openField,
        fieldValidity,
        onOpen: handleOpen,
        onClose: () => setOpenField(SearchFieldEnum.NONE),
        searchDispatch,
        searchState,
        displayType,
        searchError,
    }), [displayType, fieldValidity, handleOpen, openField, searchError, searchState]);
    const onSearchClick = useCallback((e) => {
        e.stopPropagation();
        if (validAirport) {
            const updatedState = getFilghtUpsellUpdatedState(searchState, estabId, name, isCached !== null && isCached !== void 0 ? isCached : false, isCacheEnabled);
            createSearch(updatedState);
            setOpenField(SearchFieldEnum.NONE);
        }
        else {
            setAirportError(!validAirport);
            openNextField();
            setSearchClicked(true);
        }
    }, [createSearch, estabId, isCacheEnabled, isCached, name, openNextField, searchState, validAirport]);
    return (React.createElement(SearchContext.Provider, { value: searchContext },
        React.createElement(FlexGrid, { container: true, alignItems: "flex-start", columns: 16, spacing: { xs: 4, m: 5 } },
            React.createElement(FlexGrid, { xs: 16, m: 6 },
                React.createElement(AirportAutocompleter, { state: Object.assign(Object.assign({}, departureAirportAutocompleterResults), { popupPlacement: 'bottom-start', searchClicked, fieldListId: 'flight-upsell-airport-autocompleter-list', fieldId: 'flight-upsell-airport-autocompleter-field', openAirport: SearchFieldEnum.AIRPORTS, fieldErrorMessage: SEARCH_DEPARTURE_ERROR_EMPTY }), actions: {
                        fetchData: fetchDepartureAirports,
                        onDone: openNextField,
                        setSearchClicked,
                    } })),
            React.createElement(FlexGrid, { xs: 16, m: 6 },
                React.createElement(FlightUpsellCabinSelector, null)),
            React.createElement(FlexGrid, { xs: 16, m: "fill" },
                React.createElement(Button, { fullWidth: true, size: { xs: 'xl', m: '2xl' }, "data-id": "flight-upsell-search-button", onClick: onSearchClick, loading: loading, color: "primary", variant: "outlined" }, FLIGHT_UPSELL_BUTTON_TEXT)))));
}
